import {
  fetchMFAPreference,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from "aws-amplify/auth"
import { ShieldCheck } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { PageLoader } from "@/components/page-loader"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  REGEXP_ONLY_DIGITS,
} from "@/components/ui/input-otp"
import { Typography } from "@/components/ui/typography"
import { useAuth } from "@/hooks/use-auth"

const TOTP_CODE_LENGTH = 6
const OTP_SLOT_IDS = Array.from({ length: TOTP_CODE_LENGTH }, (_, i) => `totp-slot-${i}`)

type View = "loading" | "idle" | "enrolling"

interface SetupState {
  uri: string
  secret: string
}

export default function Security() {
  const { t } = useTranslation()
  const { userAttributes, user } = useAuth()
  const accountName = userAttributes?.email ?? user?.username ?? "account"

  const [view, setView] = useState<View>("loading")
  const [totpEnabled, setTotpEnabled] = useState(false)
  const [setup, setSetup] = useState<SetupState | null>(null)
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const pref = await fetchMFAPreference()
      setTotpEnabled(pref.enabled?.includes("TOTP") ?? false)
      setView("idle")
    } catch {
      setView("idle")
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function handleStartEnrol() {
    setBusy(true)
    setError(null)
    try {
      const details = await setUpTOTP()
      setSetup({
        uri: details.getSetupUri("Legacy Keeper", accountName).toString(),
        secret: details.sharedSecret,
      })
      setCode("")
      setView("enrolling")
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("components.security.genericError"))
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirmEnrol(value: string) {
    setBusy(true)
    setError(null)
    try {
      await verifyTOTPSetup({ code: value })
      await updateMFAPreference({ totp: "PREFERRED" })
      toast.success(t("components.security.enabledToast"))
      setSetup(null)
      setCode("")
      setTotpEnabled(true)
      setView("idle")
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("components.security.invalidCode"))
      setCode("")
    } finally {
      setBusy(false)
    }
  }

  async function handleDisable() {
    setBusy(true)
    try {
      await updateMFAPreference({ totp: "DISABLED" })
      toast.success(t("components.security.disabledToast"))
      setTotpEnabled(false)
    } catch {
      toast.error(t("components.security.genericError"))
    } finally {
      setBusy(false)
    }
  }

  if (view === "loading") {
    return <PageLoader />
  }

  return (
    <Card className="p-8 rounded-xl shadow-sm border border-border space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="size-6 text-primary" />
        <Typography as="h2" size="xl">
          {t("components.security.title")}
        </Typography>
      </div>

      <Typography as="p" className="text-muted-foreground">
        {t("components.security.description")}
      </Typography>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {view === "idle" && totpEnabled && (
        <div className="space-y-4">
          <Alert>
            <AlertTitle>{t("components.security.statusEnabledTitle")}</AlertTitle>
            <AlertDescription>{t("components.security.statusEnabledBody")}</AlertDescription>
          </Alert>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={busy}>
                {t("components.security.disableButton")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("components.security.disableConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("components.security.disableConfirmBody")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDisable}>
                  {t("components.security.disableButton")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {view === "idle" && !totpEnabled && (
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTitle>{t("components.security.lockoutWarningTitle")}</AlertTitle>
            <AlertDescription>{t("components.security.lockoutWarningBody")}</AlertDescription>
          </Alert>
          <Button onClick={handleStartEnrol} disabled={busy}>
            {t("components.security.enableButton")}
          </Button>
        </div>
      )}

      {view === "enrolling" && setup && (
        <div className="space-y-6">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>{t("components.security.step1")}</li>
            <li>{t("components.security.step2")}</li>
            <li>{t("components.security.step3")}</li>
          </ol>

          <div className="flex justify-center">
            <div className="rounded-lg bg-white p-4">
              <QRCodeSVG value={setup.uri} size={180} />
            </div>
          </div>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">
              {t("components.security.manualKeyLabel")}{" "}
            </span>
            <code className="font-mono break-all">{setup.secret}</code>
          </div>

          <div className="space-y-3">
            <div className="flex justify-center">
              <InputOTP
                maxLength={TOTP_CODE_LENGTH}
                pattern={REGEXP_ONLY_DIGITS}
                value={code}
                autoFocus
                disabled={busy}
                onChange={(value) => {
                  setCode(value)
                  setError(null)
                  if (value.length === TOTP_CODE_LENGTH) {
                    handleConfirmEnrol(value)
                  }
                }}
              >
                <InputOTPGroup>
                  {OTP_SLOT_IDS.map((id, i) => (
                    <InputOTPSlot key={id} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              className="w-full"
              disabled={busy || code.length !== TOTP_CODE_LENGTH}
              onClick={() => handleConfirmEnrol(code)}
            >
              {busy
                ? t("components.security.verifyingText")
                : t("components.security.verifyButton")}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              type="button"
              disabled={busy}
              onClick={() => {
                setSetup(null)
                setCode("")
                setError(null)
                setView("idle")
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
